import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../home/components/Navbar';
import Sidebar from '../home/components/Sidebar';
import { getCreditPackages, createPurchaseOrder, completePurchase, cancelPurchaseOrder, CreditPackage } from '../../services/packages';
import { getUserCredits } from '../../services/credits';
import { buildOutTradeNo, buildPayLandingUrl, queryOrderStatus } from '../../services/wechatPay';
import { fetchAlipayPcForm, fetchAlipayWapForm, queryAlipayOrderStatus, closeAlipayOrder, submitAlipayForm } from '../../services/alipayPay';

const UA = typeof navigator !== 'undefined' ? navigator.userAgent : '';
const isWeChat = /MicroMessenger/i.test(UA);
const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(UA);

export type PayMethod = 'wechat' | 'alipay';

export default function RechargePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, refreshUser } = useAuth();
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [currentCredits, setCurrentCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  /** 支付方式选择弹窗：创建订单后先选微信/支付宝，PC 选后展示二维码，手机选后点「立即支付」重定向 */
  const [payMethodModal, setPayMethodModal] = useState<{ orderId: string; credits: number; amount: number; packageId?: string } | null>(null);
  /** 手机端在支付方式弹窗内已选中的方式（PC 不需要，直接点即展示二维码） */
  const [selectedPayMethodForModal, setSelectedPayMethodForModal] = useState<PayMethod | null>(null);
  /** PC 支付宝：请求 form 并提交时的 loading（正在跳转支付宝收银台） */
  const [alipayFormSubmitting, setAlipayFormSubmitting] = useState(false);
  /** 支付未完成提示弹窗（支付宝/微信返回后查询到取消或失败时） */
  const [payFailedDialog, setPayFailedDialog] = useState(false);
  /** 从支付页返回后「正在查询支付结果」弹窗（微信/支付宝轮询时展示） */
  const [payResultCheckingDialog, setPayResultCheckingDialog] = useState(false);
  /** 从支付页返回且支付成功后的「支付成功，积分已到账」提示弹窗 */
  const [paySuccessDialog, setPaySuccessDialog] = useState(false);
  /** PC 端：展示落地页二维码 + 轮询；含 payMethod 以区分微信/支付宝轮询 */
  const [payQrModal, setPayQrModal] = useState<{ payUrl: string; orderId: string; credits: number; amount: number; packageId?: string; payMethod: PayMethod } | null>(null);
  const [polling, setPolling] = useState(false);
  const [pollResult, setPollResult] = useState<'idle' | 'pending' | 'success'>('idle');
  /** 轮询用 setTimeout 的 id，得到响应后再隔 N 秒下一次查询 */
  const pollIntervalRef = useRef<number | null>(null);
  /** 从落地页返回后自动轮询是否已启动（避免重复） */
  const returnFromPayPollStartedRef = useRef(false);
  /** 支付宝「从支付页返回」轮询已执行次数（用于最多重试 5 次） */
  const pollAttemptRef = useRef(0);

  // 等 Auth 拉取完登录态再判断，避免刷新时 user 尚未恢复就误跳登录页
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    loadData();
  }, [authLoading, user, navigate]);

  /** silent: 为 true 时不置 loading（用于支付成功后静默刷新积分），避免全页 loading 覆盖界面 */
  const loadData = async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    const [packagesData, creditsData] = await Promise.all([
      getCreditPackages(),
      getUserCredits(user!.id),
    ]);
    setPackages(packagesData);
    setCurrentCredits(creditsData?.credits?.credits ?? 0);
    if (!options?.silent) setLoading(false);
  };

  const handlePurchase = async (pkg: CreditPackage) => {
    const log = (msg: string, data?: object) => {
      console.log('[RechargePay]', msg, data ?? '');
    };

    if (!user || purchasing) {
      log('支付按钮点击被忽略', { reason: !user ? '未登录' : '正在请求中', purchasing });
      return;
    }

    log('支付按钮点击', { packageId: pkg.id, credits: pkg.credits, price: pkg.price, isWeChat, isMobile });

    setPurchasing(true);
    setSelectedPackage(pkg.id);

    try {
      const createRes = await createPurchaseOrder(user.id, pkg.id, pkg.credits, pkg.price);
      if (!createRes.success || !createRes.orderId) {
        log('Supabase 创建订单失败', { error: createRes.error });
        alert(createRes.error || '创建订单失败');
        return;
      }
      const orderId = createRes.orderId;
      const credits = pkg.credits;
      const amount = pkg.price;
      // 手机端：微信环境默认走微信支付（不弹窗），非微信环境默认走支付宝；PC 保持弹窗选择支付方式
      if (isMobile) {
        const outTradeNo = buildOutTradeNo(orderId);
        const totalCents = Math.round(amount * 100);
        const description = `AIGC积分充值-${credits}积分`;
        if (isWeChat) {
          const backUrl = buildBackUrl(orderId, credits, amount, 'wechat');
          const payUrl = buildPayLandingUrl({
            outTradeNo,
            totalCents,
            description,
            userId: user.id,
            redirect: backUrl,
            cancel: backUrl,
          });
          window.location.href = payUrl;
          return;
        }
        setAlipayFormSubmitting(true);
        const backUrl = buildBackUrl(orderId, credits, amount, 'alipay');
        // 手机端支付宝 H5：请确保 backUrl 为 HTTPS 且可公网访问，否则支付宝回跳可能 302/404；控制台 302/跨域提示多来自支付宝 H5 页脚本，非本页代码
        console.log('[RechargePay] 请求 wap-form', { redirect: backUrl });
        const res = await fetchAlipayWapForm({
          outTradeNo,
          totalCents,
          description,
          userId: user.id,
          redirect: backUrl,
          cancel: backUrl,
        });
        setAlipayFormSubmitting(false);
        if (!res.ok) {
          alert(res.error || '获取支付宝支付页失败');
          return;
        }
        if (res.formHtml) {
          submitAlipayForm(res.formHtml);
        }
        return;
      }
      // PC：打开支付方式弹窗时，停止此前可能存在的「从支付页返回」轮询，并清除 URL 中残留的 from=pay 等参数，避免未选支付方式就轮询
      console.log('[RechargePay] PC 打开支付方式弹窗', { orderId, from: searchParams.get('from'), payOrderId: searchParams.get('payOrderId') });
      stopPolling();
      if (searchParams.get('from') === 'pay' || searchParams.get('payOrderId')) {
        returnFromPayPollStartedRef.current = false;
        navigate(window.location.pathname, { replace: true });
        console.log('[RechargePay] 已清除 URL 中 from=pay 等参数');
      }
      setSelectedPayMethodForModal(null);
      setPayMethodModal({ orderId, credits, amount, packageId: pkg.id });
    } catch (e: any) {
      console.error('[RechargePay] 购买异常', e?.message, e);
      alert(e?.message || '购买失败，请重试');
    } finally {
      setPurchasing(false);
      setSelectedPackage(null);
    }
  };

  /** 支付落地页回跳 URL（微信/支付宝共用，支付宝需带 payMethod=alipay 以便轮询时走支付宝接口） */
  const buildBackUrl = (orderId: string, credits: number, amount: number, payMethod: PayMethod) => {
    const base = `${window.location.origin}${window.location.pathname}?payOrderId=${orderId}&payCredits=${credits}&payAmount=${amount}&from=pay`;
    return payMethod === 'alipay' ? `${base}&payMethod=alipay` : base;
  };

  /** PC：选择支付方式后，微信展示二维码；支付宝请求中台 form 并渲染提交，即跳转支付宝收银台 */
  const handlePcSelectPayMethod = async (payMethod: PayMethod) => {
    if (!payMethodModal || !user) return;
    const { orderId, credits, amount, packageId } = payMethodModal;
    const outTradeNo = buildOutTradeNo(orderId);
    const totalCents = Math.round(amount * 100);
    const description = `AIGC积分充值-${credits}积分`;
    if (payMethod === 'wechat') {
      const payUrl = buildPayLandingUrl({
        outTradeNo,
        totalCents,
        description,
        userId: user.id,
      });
      setPollResult('idle');
      setPayQrModal({ payUrl, orderId, credits, amount, packageId, payMethod: 'wechat' });
      setPayMethodModal(null);
      setSelectedPayMethodForModal(null);
      return;
    }
    setPayMethodModal(null);
    setSelectedPayMethodForModal(null);
    setAlipayFormSubmitting(true);
    const redirect = buildBackUrl(orderId, credits, amount, 'alipay');
    const res = await fetchAlipayPcForm({
      outTradeNo,
      totalCents,
      description,
      userId: user.id,
      redirect,
    });
    setAlipayFormSubmitting(false);
    if (!res.ok) {
      alert(res.error || '获取支付宝支付页失败');
      return;
    }
    if (res.formHtml) {
      submitAlipayForm(res.formHtml);
    }
  };

  /** 手机：选择支付方式后点「立即支付」— 微信跳转落地页，支付宝请求中台 form 并渲染提交 */
  const handleMobileConfirmPay = async () => {
    if (!payMethodModal || !user || !selectedPayMethodForModal) {
      alert('请先选择支付方式');
      return;
    }
    const { orderId, credits, amount } = payMethodModal;
    const outTradeNo = buildOutTradeNo(orderId);
    const totalCents = Math.round(amount * 100);
    const description = `AIGC积分充值-${credits}积分`;
    const backUrl = buildBackUrl(orderId, credits, amount, selectedPayMethodForModal);

    if (selectedPayMethodForModal === 'wechat') {
      if (!isWeChat) {
        alert('请用微信打开并支付');
        return;
      }
      const payUrl = buildPayLandingUrl({
        outTradeNo,
        totalCents,
        description,
        userId: user.id,
        redirect: backUrl,
        cancel: backUrl,
      });
      window.location.href = payUrl;
      setPayMethodModal(null);
      setSelectedPayMethodForModal(null);
      return;
    }
    setAlipayFormSubmitting(true);
    const res = await fetchAlipayWapForm({
      outTradeNo,
      totalCents,
      description,
      userId: user.id,
      redirect: backUrl,
      cancel: backUrl,
    });
    setAlipayFormSubmitting(false);
    setPayMethodModal(null);
    setSelectedPayMethodForModal(null);
    if (!res.ok) {
      alert(res.error || '获取支付宝支付页失败');
      return;
    }
    if (res.formHtml) {
      submitAlipayForm(res.formHtml);
    }
  };

  /** 得到接口实际响应结果后，再隔 3 秒进行下一次查询 */
  const POLL_DELAY_AFTER_RESPONSE_MS = 3000;

  const stopPolling = () => {
    if (pollIntervalRef.current != null) {
      clearTimeout(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setPolling(false);
  };

  /** 轮询到支付成功后：更新 Supabase 订单状态并加积分（completePurchase），刷新积分与列表；若为弹窗则关闭；写入支付渠道 payment_method */
  const onPollSuccess = async (
    orderId: string,
    credits: number,
    _amount: number,
    transactionId: string,
    closeModal: boolean,
    _packageId?: string | null,
    payMethod?: PayMethod
  ) => {
    console.log('[RechargePay] onPollSuccess 被调用', { orderId, credits, transactionId, closeModal, payMethod });
    if (!user) {
      console.log('[RechargePay] onPollSuccess 跳过：无 user');
      return;
    }
    const { success, error } = await completePurchase(orderId, transactionId);
    if (success) {
      console.log('[RechargePay] completePurchase 成功', { orderId, transactionId });
    } else {
      console.error('[RechargePay] completePurchase 失败', { orderId, error });
    }
    if (!success) {
      alert(error || '更新订单失败');
      return;
    }
    // 先拉取最新积分并更新展示，再静默刷新套餐列表，避免全页 loading 或竞态导致积分未更新
    const creditsData = await getUserCredits(user.id);
    setCurrentCredits(creditsData?.credits?.credits ?? 0);
    await loadData({ silent: true });
    await refreshUser();
    window.dispatchEvent(new CustomEvent('credits-updated', { detail: { userId: user.id } }));
    if (closeModal) {
      setPollResult('success');
      setPolling(false);
      setTimeout(() => {
        setPayQrModal(null);
        setPollResult('idle');
      }, 1500);
    } else {
      // 从支付页返回（非弹窗）：弹出「支付成功，积分已到账」提示，由用户手动关闭
      setPaySuccessDialog(true);
    }
  };

  /** 启动轮询：按 payMethod 调微信/支付宝订单状态接口；成功则 onSuccess。可选 options：支付宝从支付页返回时用 maxRetries(5)+delayMs(1000)，得到回调后隔 1 秒重试，共 5 次后未成功则 onCancel(关单+更新 DB+弹窗) */
  const startPollingWithOrder = (
    orderId: string,
    credits: number,
    payMethod: PayMethod,
    onSuccess: (transactionId: string) => void,
    onCancel?: (orderId: string) => void,
    options?: { maxRetries?: number; delayMs?: number }
  ) => {
    const outTradeNo = buildOutTradeNo(orderId);
    const maxRetries = options?.maxRetries ?? 0;
    const delayMs = options?.delayMs ?? POLL_DELAY_AFTER_RESPONSE_MS;
    if (maxRetries > 0) pollAttemptRef.current = 0;
    console.log('[RechargePay] startPollingWithOrder', { orderId, payMethod, outTradeNo, maxRetries, delayMs });

    const tick = () => {
      console.log('[RechargePay] 轮询 tick 发起请求', { payMethod, outTradeNo, attempt: pollAttemptRef.current });
      const pollPromise = payMethod === 'alipay'
        ? queryAlipayOrderStatus(outTradeNo)
        : queryOrderStatus(outTradeNo);
      pollPromise
        .then((res) => {
          const isSuccess = payMethod === 'alipay'
            ? (res.ok && (res.data as { trade_status?: string })?.trade_status === 'TRADE_SUCCESS')
            : (res.ok && (res.data as { trade_state?: string })?.trade_state === 'SUCCESS');
          const transactionId = payMethod === 'alipay'
            ? (res.data as { trade_no?: string })?.trade_no
            : (res.data as { transaction_id?: string })?.transaction_id;
          console.log('[RechargePay] 轮询响应', { payMethod, ok: res.ok, isSuccess, data: res.data, error: (res as { error?: string }).error });
          if (isSuccess) {
            console.log('[RechargePay] 轮询成功，停止轮询', { transactionId });
            if (pollIntervalRef.current != null) {
              clearTimeout(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            setPolling(false);
            onSuccess(transactionId || '');
            return;
          }
          // 限定重试次数（微信/支付宝从支付页返回）：达到最大次数后执行 onCancel（微信仅更新 DB+弹窗，支付宝先关单再更新 DB+弹窗）
          if (maxRetries > 0 && onCancel) {
            pollAttemptRef.current += 1;
            if (pollAttemptRef.current >= maxRetries) {
              console.log('[RechargePay] 轮询达到最大次数，执行 onCancel', { attempt: pollAttemptRef.current, maxRetries });
              if (pollIntervalRef.current != null) {
                clearTimeout(pollIntervalRef.current);
                pollIntervalRef.current = null;
              }
              setPolling(false);
              onCancel(orderId);
              return;
            }
            console.log('[RechargePay] 轮询未成功，预约下次', { attempt: pollAttemptRef.current, maxRetries, delayMs });
          } else if (payMethod === 'alipay' && res.ok && (res.data as { trade_status?: string })?.trade_status === 'TRADE_CLOSED' && onCancel && maxRetries <= 0) {
            // 仅支付宝且无重试限制时：查询到 TRADE_CLOSED 立即关单
            console.log('[RechargePay] 支付宝 TRADE_CLOSED，立即 onCancel');
            if (pollIntervalRef.current != null) {
              clearTimeout(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            setPolling(false);
            onCancel(orderId);
            return;
          }
          pollIntervalRef.current = window.setTimeout(tick, delayMs);
        })
        .catch((err) => {
          console.log('[RechargePay] 轮询请求异常', { payMethod, outTradeNo, err: err?.message ?? err });
          // 请求失败或解析异常时也按一次重试计，继续下一轮或达到次数后 onCancel，避免只请求一次就停
          if (maxRetries > 0 && onCancel) {
            pollAttemptRef.current += 1;
            if (pollAttemptRef.current >= maxRetries) {
              console.log('[RechargePay] 轮询异常达最大次数，执行 onCancel', { attempt: pollAttemptRef.current, maxRetries });
              if (pollIntervalRef.current != null) {
                clearTimeout(pollIntervalRef.current);
                pollIntervalRef.current = null;
              }
              setPolling(false);
              onCancel(orderId);
              return;
            }
            console.log('[RechargePay] 轮询异常，预约下次', { attempt: pollAttemptRef.current, maxRetries, delayMs });
          }
          pollIntervalRef.current = window.setTimeout(tick, delayMs);
        });
    };
    setPolling(true);
    setPollResult('pending');
    tick();
  };

  // 展示二维码后自动开始轮询（PC 端弹窗场景）
  useEffect(() => {
    if (!payQrModal || !user || polling) return;
    console.log('[RechargePay] PC 二维码弹窗启动轮询', { orderId: payQrModal.orderId, payMethod: payQrModal.payMethod });
    startPollingWithOrder(payQrModal.orderId, payQrModal.credits, payQrModal.payMethod, (transactionId) => {
      onPollSuccess(payQrModal!.orderId, payQrModal!.credits, payQrModal!.amount, transactionId, true, payQrModal!.packageId, payQrModal!.payMethod);
    });
  }, [payQrModal?.orderId, payQrModal?.payMethod, user?.id]);

  // 从落地页返回（微信/支付宝）：URL 带 from=pay 时自动调中台查询支付状态，成功则 completePurchase；支付宝取消/失败则关单+更新 DB+弹窗。与 PC 端二维码弹窗轮询互斥（仅 URL 有 from=pay 时走此逻辑）。
  const fromPay = searchParams.get('from');
  const payOrderIdFromUrl = searchParams.get('payOrderId');
  const payCreditsFromUrl = searchParams.get('payCredits');
  const payAmountFromUrl = searchParams.get('payAmount');
  const payMethodFromUrl = searchParams.get('payMethod');

  useEffect(() => {
    if (fromPay !== 'pay') {
      if (fromPay != null || payOrderIdFromUrl != null) {
        console.log('[RechargePay] 从支付页返回 effect 未满足 from=pay', { fromPay, payOrderIdFromUrl });
      }
      return;
    }
    if (!user || returnFromPayPollStartedRef.current) {
      console.log('[RechargePay] 从支付页返回 effect 跳过', { hasUser: !!user, returnFromPayPollStarted: returnFromPayPollStartedRef.current });
      return;
    }
    const orderId = payOrderIdFromUrl ?? searchParams.get('payOrderId');
    const creditsStr = payCreditsFromUrl ?? searchParams.get('payCredits');
    const amountStr = payAmountFromUrl ?? searchParams.get('payAmount');
    const payMethod = (payMethodFromUrl === 'alipay' || searchParams.get('payMethod') === 'alipay' ? 'alipay' : 'wechat') as PayMethod;
    if (!orderId || creditsStr == null || amountStr == null) {
      console.log('[RechargePay] 从支付页返回 effect 缺少参数', { orderId, creditsStr, amountStr });
      return;
    }
    const credits = parseInt(creditsStr, 10);
    const amount = parseFloat(amountStr);
    if (isNaN(credits) || isNaN(amount)) {
      console.log('[RechargePay] 从支付页返回 effect 参数解析失败', { creditsStr, amountStr });
      return;
    }
    console.log('[RechargePay] 从支付页返回，启动轮询', { orderId, credits, amount, payMethod });
    returnFromPayPollStartedRef.current = true;
    setPolling(true);
    setPayResultCheckingDialog(true);
    // 支付宝：5 次后关单 + 更新 DB + 弹窗；微信：5 次后仅更新 DB + 弹窗（不调关单），逻辑分离互不影响
    const onCancelAlipay = async (oid: string) => {
      console.log('[RechargePay] onCancelAlipay 执行', { orderId: oid });
      setPayResultCheckingDialog(false);
      await closeAlipayOrder(buildOutTradeNo(oid));
      await cancelPurchaseOrder(oid);
      setPayFailedDialog(true);
      await loadData({ silent: true });
    };
    const onCancelWechat = async (oid: string) => {
      console.log('[RechargePay] onCancelWechat 执行', { orderId: oid });
      setPayResultCheckingDialog(false);
      await cancelPurchaseOrder(oid);
      setPayFailedDialog(true);
      await loadData({ silent: true });
    };
    const onSuccess = (transactionId: string) => {
      console.log('[RechargePay] 从支付页返回 onSuccess', { orderId, transactionId });
      setPayResultCheckingDialog(false);
      onPollSuccess(orderId, credits, amount, transactionId, false, undefined, payMethod);
    };
    startPollingWithOrder(
      orderId,
      credits,
      payMethod,
      onSuccess,
      payMethod === 'alipay' ? onCancelAlipay : onCancelWechat,
      { maxRetries: 5, delayMs: 1000 }
    );
  }, [user?.id, fromPay, payOrderIdFromUrl, payCreditsFromUrl, payAmountFromUrl, payMethodFromUrl]);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current != null) clearTimeout(pollIntervalRef.current);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar activeMenu="积分充值" onMenuChange={() => {}} />
        <div className="flex-1 flex flex-col min-w-0">
          <Navbar />
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <i className="ri-loader-4-line text-4xl text-teal-500 animate-spin"></i>
              <p className="mt-4 text-gray-600">加载中...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar activeMenu="积分充值" onMenuChange={() => {}} />
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <Navbar />
        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="max-w-7xl mx-auto main-content-padding">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">积分充值</h1>
              <p className="text-gray-600">选择适合您的积分套餐，解锁更多创作可能</p>
            </div>

            <div className="mb-8 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-2xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-teal-100 text-sm mb-1">当前积分余额</p>
                  <p className="text-4xl font-bold">{currentCredits}</p>
                </div>
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="ri-coin-line text-3xl"></i>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {packages.map((pkg) => (
                <div
                  key={pkg.id}
                  onClick={() => setSelectedPackage(pkg.id)}
                  className={`bg-white rounded-2xl p-6 border-2 transition-all hover:shadow-xl cursor-pointer h-full flex flex-col ${
                    selectedPackage === pkg.id
                      ? 'border-teal-500 ring-2 ring-teal-200 shadow-lg'
                      : pkg.is_popular
                        ? 'border-teal-500 shadow-lg'
                        : 'border-gray-200 hover:border-teal-300'
                  }`}
                >
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <span className="text-3xl font-bold text-teal-600">¥{pkg.price}</span>
                    </div>
                    {pkg.discount_label && (
                      <span className="inline-block bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full">
                        {pkg.discount_label}
                      </span>
                    )}
                  </div>

                  <div className="mb-6">
                    <div className="bg-teal-50 rounded-lg p-4 text-center mb-4">
                      <p className="text-2xl font-bold text-teal-600">{pkg.credits}</p>
                      <p className="text-xs text-teal-700">积分</p>
                    </div>

                    <ul className="space-y-2">
                      {(pkg.features || []).map((feature, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
                          <i className="ri-check-line text-teal-500"></i>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePurchase(pkg);
                    }}
                    disabled={purchasing}
                    className={`w-full py-3 rounded-lg font-medium transition-all whitespace-nowrap mt-auto ${
                      pkg.is_popular
                        ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white hover:from-teal-600 hover:to-emerald-600'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {purchasing && selectedPackage === pkg.id ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        处理中...
                      </span>
                    ) : (
                      '去支付'
                    )}
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-12 bg-blue-50 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-information-line text-blue-500"></i>
                购买说明
              </h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-blue-500 mt-0.5"></i>
                  <span>积分可用于所有AI创作工具，不同工具消耗积分不同</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-blue-500 mt-0.5"></i>
                  <span>PC 端点击「去支付」后选择支付方式（微信展示二维码/支付宝请求 form 并提交）；手机端微信环境默认走微信支付，非微信浏览器默认走支付宝，不弹窗。支付完成后系统将自动检测到账。</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-blue-500 mt-0.5"></i>
                  <span>支付成功后积分将自动到账，可在个人中心查看余额和消费记录</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-blue-500 mt-0.5"></i>
                  <span>如有疑问，请联系客服</span>
                </li>
              </ul>
            </div>
          </div>
        </main>
      </div>

      {/* 支付未完成提示：支付宝返回后查询到取消/失败时，关单并更新 DB 后弹出 */}
      {/* 从支付页返回后：正在查询支付结果（微信/支付宝轮询，1 秒 1 次、最多 5 次） */}
      {payResultCheckingDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 text-center">
            <i className="ri-loader-4-line text-4xl text-blue-500 animate-spin block mb-4"></i>
            <p className="text-gray-800 font-medium">正在查询支付结果</p>
            <p className="text-sm text-gray-500 mt-2">请稍候…</p>
          </div>
        </div>
      )}

      {/* 从支付页返回且支付成功：支付成功，积分已到账（用户点击确定关闭） */}
      {paySuccessDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 text-center">
            <i className="ri-checkbox-circle-fill text-4xl text-teal-500 block mb-4"></i>
            <p className="text-gray-800 font-medium">支付成功，积分已到账</p>
            <button
              type="button"
              onClick={() => setPaySuccessDialog(false)}
              className="mt-4 w-full py-2.5 rounded-xl bg-teal-500 text-white font-medium hover:bg-teal-600"
            >
              确定
            </button>
          </div>
        </div>
      )}

      {payFailedDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 text-center">
            <p className="text-gray-800 font-medium mb-6">您的订单支付未完成，请重新支付</p>
            <button
              type="button"
              onClick={() => setPayFailedDialog(false)}
              className="w-full py-2.5 rounded-xl bg-teal-500 text-white font-medium hover:bg-teal-600"
            >
              确定
            </button>
          </div>
        </div>
      )}

      {/* 支付宝 form 请求中：本系统请求中台 form 并渲染提交，即跳转支付宝收银台 */}
      {alipayFormSubmitting && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 text-center">
            <i className="ri-loader-4-line text-4xl text-blue-500 animate-spin block mb-4"></i>
            <p className="text-gray-800 font-medium">正在跳转支付宝收银台…</p>
            <p className="text-sm text-gray-500 mt-2">请稍候</p>
          </div>
        </div>
      )}

      {/* 支付方式选择弹窗：PC 选后微信展示二维码、支付宝请求 form 并提交；手机选后点「立即支付」 */}
      {payMethodModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">选择支付方式</h3>
            <p className="text-sm text-gray-500 mb-4">充值 {payMethodModal.credits} 积分 ¥{payMethodModal.amount}</p>
            {isMobile ? (
              <>
                <div className="flex flex-col gap-2 mb-4">
                  <button
                    type="button"
                    onClick={() => setSelectedPayMethodForModal('wechat')}
                    className={`w-full py-3 rounded-xl border-2 font-medium flex items-center justify-center gap-2 ${
                      selectedPayMethodForModal === 'wechat'
                        ? 'border-teal-500 bg-teal-50 text-teal-700'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <i className="ri-wechat-fill text-2xl text-green-600"></i>
                    微信支付
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedPayMethodForModal('alipay')}
                    className={`w-full py-3 rounded-xl border-2 font-medium flex items-center justify-center gap-2 ${
                      selectedPayMethodForModal === 'alipay'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <i className="ri-alipay-fill text-2xl text-blue-600"></i>
                    支付宝支付
                  </button>
                </div>
                {selectedPayMethodForModal === 'alipay' && (
                  <div className="mb-3 p-3 bg-amber-50 rounded-xl text-xs text-amber-800">
                    <p className="mb-2">若在微信等环境内无法打开支付宝 APP，可复制本页链接到系统浏览器（Safari/Chrome）打开后再支付。</p>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard?.writeText(window.location.href).then(() => alert('已复制到剪贴板，请到系统浏览器打开')).catch(() => alert('复制失败，请手动复制地址栏链接'));
                      }}
                      className="text-blue-600 underline"
                    >
                      复制本页链接
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleMobileConfirmPay}
                  disabled={!selectedPayMethodForModal}
                  className="w-full py-2.5 rounded-xl bg-teal-500 text-white font-medium hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed mb-2"
                >
                  立即支付
                </button>
              </>
            ) : (
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => handlePcSelectPayMethod('wechat')}
                  className="w-full py-3 rounded-xl border-2 border-gray-200 font-medium flex items-center justify-center gap-2 text-gray-700 hover:bg-gray-50"
                >
                  <i className="ri-wechat-fill text-2xl text-green-600"></i>
                  微信支付
                </button>
                <button
                  type="button"
                  onClick={() => handlePcSelectPayMethod('alipay')}
                  className="w-full py-3 rounded-xl border-2 border-gray-200 font-medium flex items-center justify-center gap-2 text-gray-700 hover:bg-gray-50"
                >
                  <i className="ri-alipay-fill text-2xl text-blue-600"></i>
                  支付宝支付
                </button>
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                setPayMethodModal(null);
                setSelectedPayMethodForModal(null);
              }}
              className="w-full py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 mt-2"
            >
              取消
            </button>
          </div>
        </div>
      )}

      {/* PC 端：落地页跳转二维码，展示后即自动轮询订单支付结果 */}
      {payQrModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              {payQrModal.payMethod === 'alipay' ? '支付宝扫码支付' : '微信扫码支付'}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {payQrModal.payMethod === 'alipay' ? '使用支付宝扫一扫，扫码后跳转落地页完成支付' : '使用微信扫一扫，扫码后跳转落地页完成支付'}
            </p>
            <div className="flex justify-center bg-gray-50 rounded-xl p-4 mb-4">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(payQrModal.payUrl)}`}
                alt="支付二维码"
                className="w-[220px] h-[220px]"
              />
            </div>
            <p className="text-xs text-gray-500 mb-4 text-center">
              充值 {payQrModal.credits} 积分 ¥{payQrModal.amount}，手机完成支付后系统将自动检测
            </p>
            {pollResult === 'success' && (
              <p className="text-sm text-teal-600 mb-4 text-center">支付成功</p>
            )}
            <button
              type="button"
              onClick={() => {
                stopPolling();
                setPayQrModal(null);
                setPollResult('idle');
              }}
              className="w-full py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50"
            >
              关闭
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
